import { createTheme } from '@mui/material';

const myGrey = {
  grey50: '#f8f8f8',
  grey70: '#f2f2f2cc',
  grey100: '#F2F2F2',
  grey150: '#E5E6EC',
  grey200: '#E1E1E1',
  grey300: '#D8D8D8',
  grey400: '#C2C2C2',
  grey500: '#CECECE',
  grey600: '#989898',
  grey800: '#363636',
  grey333: '#333333',
  grey4e: '#4e4e4e',
};

const myShadow = {
  menu: '#d2d2d2',
  button: '#00000040',
  cardLight: '#223c5029',
  cardDark: '#0000001a',
  hoverPrompt: '#79797933',
};

const mySocial = {
  facebook: '#1877F2',
  facebookDark: '#0866FF',
  discord: '#5865F2',
  discordDark: '#4752C4',
  reddit: '#FF4500',
  redditDark: '#FF5700',
  telegram: '#26A5E4',
  telegramDark: '#0088cc',
};

const myColors = {
  yellow: '#FFF174',
  green: '#67EB8C',
  red: '#F31C4B',
  grey: '#79747E',
};

// eslint-disable-next-line import/no-mutable-exports
export let theme = createTheme({
  breakpoints: {
    values: {
      xs: 0,
      sm: 375,
      md: 768,
      lg: 1200,
      xl: 1500,
    },
  },
  palette: {
    primary: {
      main: '#000000',
      contrastText: '#FFFFFF',
    },
    secondary: {
      main: '#262626',
    },
    info: {
      main: '#2D67FD',
      light: '#288DC6',
      dark: '#27CB99',
      contrastText: '#FF9D56',
    },
    error: {
      main: '#F31C4B',
    },
    myGrey,
    myColors,
    myShadow,
    mySocial,
  },
  typography: {
    h1: {
      fontWeight: 700,
      fontSize: 40,
      lineHeight: 'normal',
      fontStyle: 'normal',
    },
    h2: {
      fontWeight: 700,
      fontSize: 28,
      lineHeight: 'normal',
      textTransform: 'uppercase',
      textAlign: 'center',
      '&.legal-page-title': {
        textTransform: 'none',
        textAlign: 'left',
      },
    },
    h3: {
      fontWeight: 400,
      fontSize: 24,
      lineHeight: 'normal',
      textAlign: 'center',
    },
    h4: {
      fontWeight: 700,
      fontSize: 20,
      lineHeight: 'normal',
      fontStyle: 'normal',
    },
    h5: {
      fontWeight: 700,
      fontSize: 14,
      lineHeight: 'normal',
      fontStyle: 'normal',
    },
    h6: {
      fontWeight: 40,
      fontSize: 12,
      lineHeight: 'normal',
      fontStyle: 'normal',
    },
    body2: {
      fontWeight: 400,
      fontSize: 20,
      lineHeight: 'normal',
      fontStyle: 'normal',
    },
    subtitle1: {
      fontWeight: 400,
      fontSize: 18,
      lineHeight: 'normal',
      fontStyle: 'normal',
    },
    subtitle2: {
      fontWeight: 400,
      fontSize: 16,
      lineHeight: 'normal',
      fontStyle: 'normal',
    },
    caption: {
      fontWeight: 400,
      fontSize: 14,
      lineHeight: 'normal',
      fontStyle: 'normal',
    },
    button: {
      fontWeight: 700,
      fontSize: 16,
      fontStyle: 'normal',
      lineHeight: '26px',
      textAlign: 'center',
      textTransform: 'none',
    },
    overline: {
      fontWeight: 400,
      fontSize: 32,
      lineHeight: 'normal',
      fontStyle: 'normal',
    },
  },
});

const commonButtonStyle = {
  boxShadow: 'none',
  height: '56px',
  padding: '12px 16px',
  borderRadius: '8px',
  background: theme.palette.primary.main,
  color: theme.palette.primary.contrastText,
  whiteSpace: 'nowrap',
  [theme.breakpoints.down('lg')]: {
    height: 46,
    padding: '10px 14px',
    borderRadius: '6px',
  },
  '&:hover': {
    background: theme.palette.myGrey.grey800,
  },
  '&:disabled': {
    background: myGrey.grey500,
    color: theme.palette.primary.contrastText,
  },
};

const autofillSelector =
  '& .MuiInputBase-input:is(' +
  ':-webkit-autofill, :-webkit-autofill:hover, ' +
  ':-webkit-autofill:focus, :-webkit-autofill:active)';

theme = createTheme(theme, {
  typography: {
    h1: {
      [theme.breakpoints.down('md')]: {
        fontSize: 30,
      },
      [theme.breakpoints.down('sm')]: {
        fontSize: 24,
      },
    },
    h2: {
      [theme.breakpoints.down('lg')]: {
        fontSize: 22,
      },
    },
    body2: {
      [theme.breakpoints.down('lg')]: {
        fontSize: 16,
      },
      [theme.breakpoints.down('md')]: {
        fontSize: 14,
      },
    },
    subtitle1: {
      [theme.breakpoints.down('lg')]: {
        fontSize: 14,
      },
    },
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: `
        @font-face {
          font-family: 'Roboto';
          font-style: normal;
          font-weight: 400;
        }
      `,
    },
    MuiTextField: {
      defaultProps: {
        variant: 'filled',
        InputProps: {
          disableUnderline: true,
          sx: {
            borderRadius: '8px',
            minHeight: 44,
            boxSizing: 'border-box',
            [theme.breakpoints.down('lg')]: {
              minHeight: 46,
              borderRadius: '6px',
            },
          },
        },
        inputProps: {
          style: {
            padding: '9px 12px',
          },
        },
        InputLabelProps: {
          shrink: true,
          style: {
            fontWeight: 400,
            fontSize: 14,
            fontStyle: 'normal',
            lineHeight: '26px',
            color: theme.palette.primary.main,
            top: '-25px',
            left: '0px',
            transform: 'none',
          },
        },
      },
      styleOverrides: {
        root: {
          [autofillSelector]: {
            webkitBackgroundClip: 'text',
            webkitTextFillColor: theme.palette.primary.contrastText,
            transition: 'background-color 5000s ease-in-out 0s',
            boxShadow: `inset 0 0 30px 30px ${theme.palette.myGrey.grey100}`,
            borderRadius: '8px',
          },
          '& .MuiSelect-select': {
            boxSizing: 'border-box',
            maxHeight: 44,
            paddingTop: 11,
            paddingBottom: 10,
            background: 'transparent',
          },
          '& .MuiTextField-root': {
            width: '100%',
          },
          '& .MuiInputBase-multiline': {
            padding: 0,
            minHeight: 'auto',
          },
          '& .MuiInputBase-inputMultiline': {
            height: '100%',
          },
        },
      },
    },
    MuiFormHelperText: {
      styleOverrides: {
        root: {
          color: theme.palette.error.main,
          margin: 0,
          fontSize: '12px',
          fontStyle: 'normal',
          fontWeight: 400,
          lineHeight: '14px',
          paddingTop: '4px',
        },
      },
    },
    MuiModal: {
      styleOverrides: {
        root: {
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          color: 'inherit',
        },
      },
    },
    MuiButton: {
      defaultProps: {
        variant: 'contained',
        sx: {
          overflow: 'hidden',
        },
      },
      variants: [
        {
          props: { variant: 'defaultLight' },
          style: {
            boxShadow: 'none',
            height: 56,
            padding: '12px 16px',
            whiteSpace: 'nowrap',
            borderRadius: 8,
            background: theme.palette.myGrey.grey200,
            color: theme.palette.primary.main,
            columnGap: '8px',
            '&:hover': {
              background: theme.palette.myGrey.grey500,
            },
            [theme.breakpoints.down('lg')]: {
              height: 46,
              padding: '10px 14px',
              borderRadius: 6,
            },
          },
        },
        {
          props: { variant: 'transparent' },
          style: {
            ...commonButtonStyle,
            background: 'transparent',
            color: theme.palette.primary.main,
            '&:hover': {
              background: theme.palette.myGrey.grey100,
            },
          },
        },
        {
          props: { variant: 'autoSize' },
          style: {
            ...commonButtonStyle,
          },
        },
        {
          props: { variant: 'maxSize' },
          style: {
            ...commonButtonStyle,
            width: '100%',
          },
        },
        {
          props: { variant: 'fixedSize' },
          style: {
            ...commonButtonStyle,
            width: '65%',
            '@media (max-width:500px)': {
              width: '100%',
            },
          },
        },
        {
          props: { variant: 'link' },
          style: {
            boxShadow: 'none',
            width: 'fit-content',
            background: 'transparent',
            border: 'none',
            textAlign: 'right',
            fontSize: 12,
            fontWeight: 400,
            lineHeight: 'normal',
            fontStyle: 'normal',
            color: theme.palette.info.main,
            padding: 0,
            transition: 'opacity 0.2s ease',
            '&:hover': {
              background: 'transparent',
              opacity: 0.5,
            },
          },
        },
        {
          props: { variant: 'menu' },
          style: {
            boxShadow: 'none',
            width: '100%',
            display: 'flex',
            justifyContent: 'space-between',
            columnGap: '10px',
            alignItems: 'center',
            background: 'transparent',
            border: 'none',
            textAlign: 'center',
            fontFamily: 'Inter, sans-serif',
            fontSize: 12,
            fontWeight: 400,
            lineHeight: 'normal',
            fontStyle: 'normal',
            color: theme.palette.primary.main,
            padding: '13px 12px',
            [theme.breakpoints.down('lg')]: {
              padding: '8px 12px',
            },
            transition: 'background-color ease-in-out 0.2s',
            '&:hover': {
              background: theme.palette.myGrey.grey100,
            },
          },
        },
        {
          props: { variant: 'conferenceIcon' },
          style: {
            width: '56px',
            minWidth: '56px',
            height: '56px',
            background: 'transparent',
            border: 'none',
            borderRadius: '0px',
            transition: 'all ease-in-out 0.2s',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            padding: 0,
            '&.open-menu': {
              width: '32px',
              minWidth: '32px',
              borderLeft: `1px solid ${theme.palette.myGrey.grey100}`,
            },
            '&:hover': {
              background: theme.palette.myGrey.grey100,
            },
            [theme.breakpoints.down('lg')]: {
              height: '46px',
              width: '44px',
              minWidth: '44px',
              '&.open-menu': {
                width: '26px',
                minWidth: '26px',
              },
            },
          },
        },
        {
          props: { variant: 'leave' },
          style: {
            ...commonButtonStyle,
            background: theme.palette.error.main,
            '&:hover': {
              background: theme.palette.error.main,
            },
          },
        },
        {
          props: { variant: 'mainMenu' },
          style: {
            boxShadow: 'none',
            minHeight: 56,
            minWidth: 'min-content',
            padding: '0 12px',
            borderRadius: 8,
            background: theme.palette.primary.contrastText,
            color: theme.palette.primary.main,
            fontWeight: 400,
            textTransform: 'none',
            display: 'flex',
            justifyContent: 'flex-start',
            whiteSpace: 'nowrap',
            [theme.breakpoints.down('lg')]: {
              fontSize: 0,
              borderRadius: 6,
            },
            '& svg': {
              marginRight: 8,
              [theme.breakpoints.down('lg')]: {
                marginRight: 0,
              },
            },
            '&:hover': {
              background: theme.palette.myGrey.grey100,
            },
            '&.selected': {
              fontWeight: 700,
              background: theme.palette.myGrey.grey100,
            },
          },
        },
        {
          props: { variant: 'mobileMenu' },
          style: {
            boxShadow: 'none',
            borderRadius: 0,
            minHeight: 75,
            width: '20%',
            padding: '18px 0',
            background: theme.palette.primary.contrastText,
            color: theme.palette.myGrey.grey600,
            fontWeight: 400,
            fontSize: 12,
            lineHeight: '14px',
            textTransform: 'none',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'flex-start',
            rowGap: '4px',
            flexGrow: 1,
            flexShrink: 1,
            whiteSpace: 'nowrap',
            '&:hover': {
              background: theme.palette.primary.contrastText,
            },
            '&.selected': {
              fontWeight: 700,
              color: theme.palette.primary.main,
            },
          },
        },
      ],
    },
    MuiTabs: {
      styleOverrides: {
        root: {
          '&.modal': {
            width: '100%',
            minHeight: '32px',
            height: '32px',
            padding: '0 ',
            boxShadow: 'none',
            borderRadius: '0px',
            top: '50%',
            '& .MuiTabs-flexContainer': {
              maxHeight: '32px',
              borderBottom: '2px solid',
              borderColor: theme.palette.myGrey.grey150,
            },
            '& .MuiTabs-indicator': {
              backgroundColor: theme.palette.info.main,
              height: '2px',
              width: '50%',
              margingTop: '6px',
              zIndex: 1,
            },
            '& button': {
              padding: '0 0 20px',
              maxHeight: '32px',
              background: 'transparent',
              color: theme.palette.primary.main,
              textTransform: 'none',
              fontWeight: 400,
              borderRadius: '8px',
            },
            '& button:hover': {
              background: theme.palette.myGrey.grey150,
            },
            '& button.Mui-selected': {
              fontWeight: 700,
            },
          },
          '&.profileTabs': {
            width: '30vw',
            minWidth: '335px',
            [theme.breakpoints.down('lg')]: {
              width: '50vw',
              minWidth: 'auto',
            },
            [theme.breakpoints.down('md')]: {
              width: '91vw',
            },
            [theme.breakpoints.down('sm')]: {
              width: '91vw',
            },
          },
        },
      },
    },
    MuiLink: {
      styleOverrides: {
        root: {
          fontSize: 20,
          fontWeight: 400,
          fontStyle: 'normal',
          lineHeight: 'normal',
          color: theme.palette.primary.main,
          textDecoration: 'none',
          '&:hover': {
            textDecoration: 'none',
            color: theme.palette.myGrey.grey800,
          },
          [theme.breakpoints.down('lg')]: {
            fontSize: 16,
          },
          [theme.breakpoints.down('md')]: {
            fontSize: 14,
          },
        },
      },
    },
    MuiList: {
      styleOverrides: {
        root: {
          '&.list-legal-pages': {
            padding: 0,
          },
        },
      },
    },
    MuiListItem: {
      styleOverrides: {
        root: {
          '&.list-legal-pages': {
            padding: 0,
            columnGap: '12px',
            alignItems: 'flex-start',
            '& .MuiListItemIcon-root': {
              minWidth: 'min-content',
              paddingTop: '14px',
              [theme.breakpoints.down('lg')]: {
                paddingTop: '9px',
              },
            },
            '& .MuiListItemText-root': {
              fontSize: 20,
              [theme.breakpoints.down('lg')]: {
                fontSize: 16,
              },
              [theme.breakpoints.down('md')]: {
                fontSize: 14,
              },
            },
          },
        },
      },
    },
  },
});

type MyPaletteExtensions = {
  myGrey: typeof myGrey;
  myColors: typeof myColors;
  myShadow: typeof myShadow;
  mySocial: typeof mySocial;
};

declare module '@mui/material/styles' {
  interface Palette extends MyPaletteExtensions {}
  interface PaletteOptions extends MyPaletteExtensions {}
}

declare module '@mui/material/Button' {
  interface ButtonPropsVariantOverrides {
    defaultLight: true;
    transparent: true;
    autoSize: true;
    maxSize: true;
    fixedSize: true;
    link: true;
    menu: true;
    conferenceIcon: true;
    leave: true;
    mainMenu: true;
    mobileMenu: true;
  }
}
